import type { RealtimeChannel } from "@supabase/supabase-js";
import type { PeerMediaSignal } from "@topzle/realtime";
import { supabase } from "../supabase";

interface BroadcastEnvelope extends PeerMediaSignal {
  fromId: string;
  sentAt: number;
}

function isSignal(value: unknown): value is BroadcastEnvelope {
  if (!value || typeof value !== "object") return false;
  const signal = value as Partial<BroadcastEnvelope>;
  const envelopeValid = Boolean(
    typeof signal.signalId === "string" &&
      signal.signalId.length <= 80 &&
      typeof signal.fromId === "string" &&
      signal.fromId.length <= 128 &&
      typeof signal.targetId === "string" &&
      signal.targetId.length <= 128 &&
      typeof signal.sentAt === "number" &&
      [signal.description, signal.candidate, signal.preferences].filter(Boolean).length === 1,
  );
  if (!envelopeValid) return false;
  if (signal.description)
    return Boolean(
      ["offer", "answer"].includes(signal.description.type ?? "") &&
        typeof signal.description.sdp === "string" &&
        signal.description.sdp.length > 0 &&
        signal.description.sdp.length <= 56_000,
    );
  if (signal.candidate)
    return (
      typeof signal.candidate.candidate === "string" && signal.candidate.candidate.length <= 4_096
    );
  return typeof signal.preferences?.receiveVideo === "boolean";
}

/**
 * Ephemeral contingency transport for WebRTC offers, answers and ICE only.
 * The HMAC-derived topic is returned only after joining the room. No chat or
 * authoritative game state is duplicated into Supabase.
 */
export class SupabasePeerSignalBus {
  private channel: RealtimeChannel | null = null;

  constructor(
    private readonly topicToken: string,
    private readonly participantId: string,
    private readonly onSignal: (signal: PeerMediaSignal) => void,
  ) {}

  start(subscribe = true): void {
    if (!supabase || this.channel) return;
    const channel = supabase.channel(`room-media:${this.topicToken}`, {
      config: { broadcast: { ack: true, self: false } },
    });
    this.channel = channel;
    channel.on("broadcast", { event: "peer-signal" }, ({ payload }) => {
      if (
        !isSignal(payload) ||
        payload.targetId !== this.participantId ||
        payload.fromId === this.participantId ||
        Date.now() - payload.sentAt > 30_000
      )
        return;
      this.onSignal(payload);
    });
    if (subscribe) channel.subscribe();
  }

  async send(
    targetId: string,
    signal: Omit<PeerMediaSignal, "fromId" | "targetId">,
  ): Promise<boolean> {
    if (!this.channel || !signal.signalId) return false;
    // supabase-js sends over HTTP when this client is not subscribed. That
    // lets a healthy sender reach a receiver whose room socket has failed
    // without opening a fallback WebSocket for every healthy player.
    const result = await this.channel.send({
      type: "broadcast",
      event: "peer-signal",
      payload: {
        ...signal,
        fromId: this.participantId,
        targetId,
        sentAt: Date.now(),
      } satisfies BroadcastEnvelope,
    });
    return result === "ok";
  }

  stop(): void {
    const channel = this.channel;
    this.channel = null;
    if (channel && supabase) void supabase.removeChannel(channel);
  }
}
